import ChevronDownIcon from '@src/svgs/ChevronDownIcon';
import React, { useState } from 'react';
import styled from 'styled-components';

import type { CSSProperties } from 'react';

export type TooltipEntry = {
  label: string;
  value: string;
  /** Optional trailing metadatum shown in muted (darker) text at the row's end
   * — e.g. how far a slid dub segment moved (`"+1.3s"`) (PLAT-10732). */
  note?: string;
};

/**
 * An optional collapsible section rendered under the entries: a clickable
 * caret header ("▸ {label}  {count}") that expands its `rows` on click. Used by
 * the dub-info overlay to keep the (potentially long) slid / sped-up segment
 * lists hidden until the user asks for them (PLAT-10732).
 */
export type TooltipCollapsible = {
  label: string;
  rows: TooltipEntry[];
};

/**
 * An optional titled sub-section rendered under the main entries: a muted
 * sub-heading, then plain `entries`, then any `collapsibles`. The dub-info
 * overlay uses it for the "Adjustments to fit voice in timeline" group
 * (PLAT-10719/10732).
 */
export type TooltipSection = {
  title: string;
  entries?: TooltipEntry[];
  collapsibles?: TooltipCollapsible[];
};

export const DARK_TOOLTIP_STYLE: CSSProperties = {
  padding: 0,
  background: 'none',
  border: 'none',
  boxShadow: 'none',
  zIndex: 100005,
};

export function ClipContextTooltip({
  entries,
  title,
  titleValue,
  rows,
  section,
  sections,
}: {
  entries: TooltipEntry[];
  title?: string;
  /** Rendered on the right of the title row (e.g. the dub language across the
   * "Dub Info" header) so it reads as a single top row (PLAT-10732). */
  titleValue?: string;
  /**
   * A plain single-line list under the entries — position, ellipsized text,
   * trailing muted note — the reel overlay's clip list. The same row shape the
   * collapsibles use, without the disclosure: a reel is a handful of clips,
   * and hiding the one thing the overlay exists to say defeats it.
   */
  rows?: TooltipEntry[];
  section?: TooltipSection;
  /**
   * Further titled sub-sections after `section`, in order. The dub-info overlay
   * uses one for the take's direction (PLAT-11799), which is a different kind
   * of fact from the fitting adjustments and reads wrongly folded in with them.
   */
  sections?: TooltipSection[];
}) {
  const sectionCollapsibles = (section?.collapsibles ?? []).filter(
    c => c.rows.length > 0,
  );
  const extraCollapsibles = (sections ?? []).flatMap(s =>
    (s.collapsibles ?? []).filter(c => c.rows.length > 0),
  );
  return (
    // Swallow mousedown so a click on a caret can't reach the language
    // dropdown's document-level outside-click handler and dismiss the menu the
    // green-mic anchor lives in.
    <TooltipContainer
      $fixed={!!section || !!sections?.length}
      onMouseDown={
        sectionCollapsibles.length || extraCollapsibles.length
          ? e => e.stopPropagation()
          : undefined
      }
    >
      {(title || titleValue) && (
        <TooltipTitle>
          {title && <TitleLabel>{title}</TitleLabel>}
          {titleValue && <TitleValue>{titleValue}</TitleValue>}
        </TooltipTitle>
      )}
      {entries.map((entry, i) => (
        // Index in the key: some rows (e.g. the dub segment lists) can repeat a
        // label (two segments at the same timecode).
        <TooltipRow key={`${entry.label}-${i}`}>
          <TooltipLabel>{entry.label}</TooltipLabel>
          <TooltipValue>{entry.value}</TooltipValue>
        </TooltipRow>
      ))}
      {rows?.map((row, i) => (
        <SegmentRow key={`row-${row.label}-${i}`}>
          <TooltipLabel>{row.label}</TooltipLabel>
          <SegmentText>{row.value}</SegmentText>
          {row.note && <TooltipNote>{row.note}</TooltipNote>}
        </SegmentRow>
      ))}
      {section && (
        <>
          <TooltipSubTitle>{section.title}</TooltipSubTitle>
          {section.entries?.map((entry, i) => (
            <TooltipRow key={`section-${entry.label}-${i}`}>
              <TooltipLabel>{entry.label}</TooltipLabel>
              <TooltipValue>{entry.value}</TooltipValue>
            </TooltipRow>
          ))}
          {sectionCollapsibles.map(c => (
            <CollapsibleSection key={c.label} section={c} />
          ))}
        </>
      )}
      {sections?.map(extra => (
        <React.Fragment key={`extra-${extra.title}`}>
          <TooltipSubTitle>{extra.title}</TooltipSubTitle>
          {extra.entries?.map((entry, i) => (
            <SegmentRow key={`extra-${extra.title}-${entry.label}-${i}`}>
              <TooltipLabel>{entry.label}</TooltipLabel>
              <SegmentText>{entry.value}</SegmentText>
              {entry.note && <TooltipNote>{entry.note}</TooltipNote>}
            </SegmentRow>
          ))}
          {(extra.collapsibles ?? [])
            .filter(c => c.rows.length > 0)
            .map(c => (
              <CollapsibleSection
                key={`${extra.title}-${c.label}`}
                section={c}
              />
            ))}
        </React.Fragment>
      ))}
    </TooltipContainer>
  );
}

function CollapsibleSection({ section }: { section: TooltipCollapsible }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <CollapseHeader
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
      >
        <TooltipLabel>{section.label}</TooltipLabel>
        <Caret aria-hidden="true" data-open={open}>
          <ChevronDownIcon width="10" height="10" />
        </Caret>
        <CollapseCount>{section.rows.length}</CollapseCount>
      </CollapseHeader>
      {open &&
        section.rows.map((row, i) => (
          // Segment rows stay on ONE line — timecode | ellipsized text | shift
          // note — so a long snippet can't wrap and topple the layout
          // (PLAT-10732).
          <SegmentRow key={`${section.label}-${row.label}-${i}`}>
            <TooltipLabel>{row.label}</TooltipLabel>
            <SegmentText>{row.value}</SegmentText>
            {row.note && <TooltipNote>{row.note}</TooltipNote>}
          </SegmentRow>
        ))}
    </>
  );
}

/** Exported so sibling rich-hover panels (TranscriptExcerptPanel) share the
 * same chrome instead of drifting on their own copy. */
export const TooltipContainer = styled.div<{ $fixed?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  padding: var(--spacing-sm) var(--spacing-md);
  /* Sectioned overlays (dub info) use a fixed width so expanding a collapsible
     row never changes the window width; simple tooltips size to content. */
  ${p =>
    p.$fixed
      ? 'width: 300px; max-width: 300px;'
      : 'min-width: 180px; max-width: 320px;'}
  /* Cap the height so expanding a long collapsible list scrolls inside the
     overlay instead of growing (and shifting) the whole window. */
  max-height: 340px;
  overflow-y: auto;
  overscroll-behavior: contain;
  /* The standard tooltip surface (tooltipStyles.ts TOOLTIP_STYLE): the popover
     gray reads as a layer above the page, where the page's own background made
     the overlay look flat against the card behind it. */
  background: var(--popover);
  border: 1px solid var(--gray-600);
  border-radius: var(--radius);
  box-shadow: var(--elevation-md);
`;

// Top row of the overlay: the title on the left, an optional value (the dub
// language) pushed to the right so they share a single header row (PLAT-10732).
const TooltipTitle = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: var(--spacing-md);
  padding-bottom: var(--spacing-2xs);
  border-bottom: 1px solid var(--border);
  margin-bottom: var(--spacing-2xs);
`;

/* Title Case, never uppercased (docs/design-system.md — no all-caps text). */
const TitleLabel = styled.span`
  font-size: var(--text-xs);
  font-weight: var(--font-weight-bold);
  color: var(--foreground);
`;

// The header's right-hand value (dub language) — Title Case, not uppercased.
const TitleValue = styled.span`
  font-size: var(--text-xs);
  font-weight: var(--font-weight-bold);
  color: var(--foreground);
  text-align: right;
`;

// Sub-heading for a grouped section (e.g. the dub "Adjustments to fit voice in
// timeline"). Muted vs. the main title; a divider sets the group apart.
const TooltipSubTitle = styled.div`
  font-size: var(--text-xs);
  font-weight: var(--font-weight-bold);
  color: var(--text-muted);
  padding-top: var(--spacing-2xs);
  margin-top: var(--spacing-2xs);
  border-top: 1px solid var(--border);
`;

const TooltipRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: var(--spacing-md);
`;

const TooltipLabel = styled.span`
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  color: var(--text-muted);
  white-space: nowrap;
  flex-shrink: 0;
`;

const TooltipValue = styled.span`
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  color: var(--foreground);
  text-align: right;
  word-break: break-word;
  /* Honor \n in a value so a row can list one item per line (e.g. the dub
     "Force Fit" row); single-line values are unaffected. */
  white-space: pre-line;
`;

// Clickable header for a collapsible section: label left, count + disclosure
// chevron pushed to the right edge (the chevron sits at the END of the row).
const CollapseHeader = styled.button`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  width: 100%;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  text-align: left;
  font: inherit;
`;

// Disclosure chevron at the end of the header. Points right when collapsed,
// rotates down when expanded.
const Caret = styled.span`
  display: inline-flex;
  align-items: center;
  color: var(--text-muted);
  transition: transform 120ms ease-out;
  transform: rotate(-90deg);
  &[data-open='true'] {
    transform: rotate(0deg);
  }
`;

const CollapseCount = styled.span`
  margin-left: auto;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  color: var(--foreground);
`;

// A collapsible segment row, forced onto ONE line: fixed timecode, an
// ellipsized text column, and a fixed trailing note (PLAT-10732).
const SegmentRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: var(--spacing-sm);
`;

// The segment's text — takes the remaining width and truncates with an
// ellipsis so the row never wraps.
const SegmentText = styled.span`
  flex: 1;
  min-width: 0;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  color: var(--foreground);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// Trailing metadatum on a collapsible row (e.g. a slid segment's shift amount).
// Muted so it reads as darker/secondary next to the segment text.
const TooltipNote = styled.span`
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  flex-shrink: 0;
`;
